<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { useRoute, RouterView } from 'vue-router'
import { useStoreSubscriptions } from '@/composables/useStoreSubscriptions'
import { useLocale } from '@/composables/useLocale'
import { useMachineStore } from '@/stores/machineStore'
import { useUpdater } from '@/composables/useUpdater'
import {
  startUpdatePolicy,
  setUpdatePolicy,
  clearUpdateTimers,
  unbindUpdateResumeListeners,
} from '@/composables/useUpdatePolicy'
import { loadCardshopOps } from '@/utils/cardshopOps'
import { getMachineCodeFallback } from '@/utils/env'
import { reconnectKioskWs } from '@/composables/useKioskWs'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import FullscreenLayout from '@/layouts/FullscreenLayout.vue'
import ToastContainer from '@/components/toast/ToastContainer.vue'
import UpdateModal from '@/components/common/UpdateModal.vue'

// 初始化语言（读 localStorage → 设置 locale + html[lang]）
// 必须在 i18n plugin 注册后调用（main.ts 已 app.use(i18n) 完成）
useLocale().initLocale()

// 启动时异步初始化机器码（不依赖 WS 连接）
// 优先级：本机已粘贴的后台短码 > localStorage > 浏览器 fallback（瘦 H5）
onMounted(async () => {
  const machineStore = useMachineStore()
  const lsKey = 'kiosk_machine_code'

  let finalCode = localStorage.getItem(lsKey) || ''

  try {
    const { isTauri, getMachineId, enableAutostart, disableAutostart } = await import('@/utils/tauri-bridge')
    if (isTauri()) {
      const configured = await getMachineId()
      if (configured?.trim()) {
        finalCode = configured.trim()
      }
      // 开机自启：按 ops 默认开
      try {
        const ops = loadCardshopOps()
        if (ops.autostart_enabled) await enableAutostart()
        else await disableAutostart()
      } catch (e) {
        console.warn('[App] autostart sync skipped', e)
      }
      // 本机 COM 纸钞（通道非 com 时内部 noop）
      try {
        const { bootstrapBillAcceptor } = await import('@/composables/useCardshopBillAcceptor')
        void bootstrapBillAcceptor()
      } catch (e) {
        console.warn('[App] bill acceptor bootstrap skipped', e)
      }
    }
  } catch (err) {
    console.warn('[App] 读取本机机台码失败，将使用已保存配置', err)
  }

  if (!finalCode && !isTauriRuntime()) {
    finalCode = getMachineCodeFallback()
  }

  if (finalCode) {
    try {
      machineStore.setCode(finalCode)
      localStorage.setItem(lsKey, finalCode)
      reconnectKioskWs()
    } catch (err) {
      console.error('[App] 设置机器码失败', err)
    }
  }
})

function isTauriRuntime(): boolean {
  return typeof window !== 'undefined' && (
    '__TAURI__' in window ||
    '__TAURI_INTERNALS__' in window
  )
}

// 启动 WS 事件 → store action 绑定（全局单例，只调用一次）
useStoreSubscriptions()

// 自动更新策略
const { checkUpdate, installUpdate, updateInfo, isUpdating } = useUpdater()
const showUpdateModal = ref(false)

async function runSilentUpdateCheck() {
  try {
    const info = await checkUpdate()
    if (info?.available) showUpdateModal.value = true
  } catch (e) {
    console.warn('[App] silent update check failed', e)
  }
}

onMounted(() => {
  const ops = loadCardshopOps()
  setUpdatePolicy({
    mode: ops.update_check_mode,
    weeklyAt: ops.update_check_daily_at,
    weeklyDow: ops.update_check_weekly_dow,
  })
  startUpdatePolicy(runSilentUpdateCheck)
})

onUnmounted(() => {
  clearUpdateTimers()
  unbindUpdateResumeListeners()
})

/**
 * 处理更新
 */
async function handleUpdate() {
  await installUpdate()
}

const route = useRoute()

// 根据 route.meta.layout 切换 Layout
const currentLayout = computed(() => {
  return route.meta['layout'] === 'fullscreen' ? FullscreenLayout : DefaultLayout
})
</script>

<template>
  <div class="app-wrapper">
    <!--
      Layout 切换：根据 meta.layout 渲染不同骨架
      RouterView v-slot 将 Component 传入 Layout 的默认 slot
    -->
    <RouterView v-slot="{ Component }">
      <component :is="currentLayout">
        <Transition name="page" mode="out-in">
          <component :is="Component" />
        </Transition>
      </component>
    </RouterView>

    <!-- 全局 Toast 容器（E.3-2 正式实装） -->
    <ToastContainer />

    <!-- 更新提示 -->
    <UpdateModal
      :visible="showUpdateModal"
      :update-info="updateInfo"
      :is-updating="isUpdating"
      @close="showUpdateModal = false"
      @update="handleUpdate"
    />
  </div>
</template>

<style>
/* 全局基础：防止双击放大 */
* {
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
}
</style>
